import React from 'react'
import {
  Card,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { BillingSummaryResponse, BillingPlan } from '../../services/api'
import { formatDateDDMMYYYY } from '../../utils/dateUtils'

export interface BillingHistoryTableProps {
  summary: BillingSummaryResponse | null
  plans?: BillingPlan[]
}

const getStatusChip = (status: string) => {
  const normalized = (status || '').toLowerCase()
  const chipStyles = { color: 'white', fontWeight: 800, fontSize: '0.66rem' }

  switch (normalized) {
    case 'paid':
      return <Chip label="PAID" color="success" size="small" sx={chipStyles} />
    case 'failed':
      return <Chip label="FAILED" color="error" size="small" sx={chipStyles} />
    case 'pending':
      return <Chip label="PENDING" color="warning" size="small" sx={chipStyles} />
    default:
      return <Chip label={(status).toUpperCase()} color="default" size="small" sx={chipStyles} />
  }
}

export const BillingHistoryTable: React.FC<BillingHistoryTableProps> = ({ summary, plans }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const historyItems = (summary?.billing_history || []).map((tx) => {
    const matchingPlan = plans?.find((p) => p.sku === tx.plan_type)
    const planName = matchingPlan?.display_metadata?.title || (tx.plan_type?.toLowerCase().includes('topup') ? 'Top-Up Credits' : (tx.plan_type || 'Plan'))
    return {
      id: tx.transaction_id,
      date: tx.created_at,
      planName,
      amount: tx.amount_subunits / 100,
      status: tx.status,
    }
  })

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: '#e2e8f0', borderRadius: 4, p: { xs: 2.5, sm: 3.5 }, bgcolor: '#ffffff' }}>
      <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
        Payment History
      </Typography>
      <Typography variant="caption" color="#64748b" sx={{ display: 'block', mb: 3, fontSize: isMobile ? '0.775rem' : '0.875rem', lineHeight: 1.4 }}>
        View past payments and transaction details
      </Typography>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: '#e2e8f0', borderRadius: 3, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 550 }}>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' }}>Plan Name</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historyItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#64748b' }}>
                  No transactions yet.
                </TableCell>
              </TableRow>
            ) : (
              historyItems.map((item) => (
                <TableRow key={item.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                    {formatDateDDMMYYYY(item.date)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>
                    {item.planName}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#0f172a' }}>
                    ₹{item.amount.toLocaleString('en-IN')}.00
                  </TableCell>
                  <TableCell>
                    {getStatusChip(item.status)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  )
}
